// Schema.org JSON-LD injection. Kept separate from the OG/Twitter head manager.

import { SITE_NAME, SITE_OG_IMAGE, absoluteUrl } from "./site";

const CONTEXT = "https://schema.org";
const LANGUAGES = ["en", "fr", "da", "de"];

export interface FaqItem {
  question: string;
  answer: string;
}

export interface BreadcrumbItem {
  name: string;
  path: string;
}

function websiteRef(): object {
  return {
    "@type": "WebSite",
    name: SITE_NAME,
    url: absoluteUrl("/"),
    inLanguage: LANGUAGES,
  };
}

export function buildWebSite(): object {
  return {
    "@context": CONTEXT,
    "@type": "WebSite",
    name: SITE_NAME,
    url: absoluteUrl("/"),
    inLanguage: LANGUAGES,
  };
}

export function buildOrganization(): object {
  return {
    "@context": CONTEXT,
    "@type": "Organization",
    name: SITE_NAME,
    url: absoluteUrl("/"),
    logo: SITE_OG_IMAGE,
  };
}

export function buildFaqPage(items: FaqItem[]): object {
  return {
    "@context": CONTEXT,
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function buildWebPage(input: {
  title: string;
  description: string;
  path: string;
}): object {
  return {
    "@context": CONTEXT,
    "@type": "WebPage",
    name: input.title,
    description: input.description,
    url: absoluteUrl(input.path),
    isPartOf: websiteRef(),
  };
}

export function buildCollectionPage(input: {
  title: string;
  description: string;
  path: string;
}): object {
  return {
    "@context": CONTEXT,
    "@type": "CollectionPage",
    name: input.title,
    description: input.description,
    url: absoluteUrl(input.path),
    isPartOf: websiteRef(),
  };
}

export function buildBreadcrumb(items: BreadcrumbItem[]): object {
  return {
    "@context": CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function buildArticleSchema(input: {
  title: string;
  description: string;
  path: string;
  datePublished: string;
}): object {
  return {
    "@context": CONTEXT,
    "@type": "Article",
    headline: input.title,
    description: input.description,
    url: absoluteUrl(input.path),
    mainEntityOfPage: absoluteUrl(input.path),
    image: SITE_OG_IMAGE,
    datePublished: input.datePublished,
    dateModified: input.datePublished,
    author: {
      "@type": "Organization",
      name: SITE_NAME,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: SITE_OG_IMAGE,
    },
    isPartOf: websiteRef(),
  };
}

export function applyJsonLd(schemas: object[]): void {
  const id = "seo-jsonld";
  const existing = document.getElementById(id);
  if (existing) existing.remove();

  if (!schemas.length) return;

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.textContent = JSON.stringify(
    schemas.length === 1 ? schemas[0] : schemas
  );
  document.head.appendChild(script);
}